import type { FC, ReactNode } from 'react'

import { BrandLockup } from '@/components/generals/brand-mark'
import { Container } from '@/components/generals/container'
import { Ruler } from '@/components/generals/ruler'
import { formatClock } from '@/lib/format'

import {
    directions,
    garage,
    navLinks,
    openingHours
} from '@/constants/garage'

export const SiteFooter: FC = (): ReactNode => {
    const weekday = openingHours.find(day => day.day === 1)
    const saturday = openingHours.find(day => day.day === 6)

    return (
        <footer data-theme='ink' className='overflow-hidden bg-background text-foreground'>
            <Container className='pb-28 pt-16 lg:pb-12 lg:pt-24'>
                <div className='grid gap-12 lg:grid-cols-12'>
                    <div className='lg:col-span-4'>
                        <BrandLockup inverted />

                        <p className='mt-6 max-w-[22rem] text-muted-foreground'>
                            Mécanique toutes marques sur l’île de Nantes depuis {garage.founded}. Prix affichés, devis écrit, voiture rendue le jour promis.
                        </p>
                    </div>

                    <div className='grid gap-10 sm:grid-cols-3 lg:col-span-8'>
                        <div>
                            <h2 className='label-mono text-muted-foreground'>
                                Contact
                            </h2>

                            <address className='mt-4 space-y-2 not-italic'>
                                <a href={garage.phone.href} className='heading block text-xl tabular-nums tracking-[-0.01em] hover:text-signal-text'>
                                    {garage.phone.display}
                                </a>

                                <p className='text-muted-foreground'>
                                    {garage.address.street}
                                    <br />
                                    {garage.address.postalCode} {garage.address.city}
                                </p>

                                <a
                                    href={directions.google}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='inline-flex min-h-11 items-center underline decoration-foreground/30 underline-offset-4 hover:decoration-foreground'
                                >
                                    Itinéraire
                                </a>
                            </address>
                        </div>

                        <div>
                            <h2 className='label-mono text-muted-foreground'>
                                Horaires
                            </h2>

                            <dl className='mt-4 space-y-2'>
                                <div className='flex justify-between gap-4'>
                                    <dt>Lun. au ven.</dt>
                                    <dd className='tabular-nums text-muted-foreground'>
                                        {formatClock(weekday?.open ?? 0)} · {formatClock(weekday?.close ?? 0)}
                                    </dd>
                                </div>

                                <div className='flex justify-between gap-4'>
                                    <dt>Samedi</dt>
                                    <dd className='tabular-nums text-muted-foreground'>
                                        {formatClock(saturday?.open ?? 0)} · {formatClock(saturday?.close ?? 0)}
                                    </dd>
                                </div>

                                <div className='flex justify-between gap-4'>
                                    <dt>Dimanche</dt>
                                    <dd className='text-muted-foreground'>Fermé</dd>
                                </div>
                            </dl>
                        </div>

                        <nav aria-label='Plan du site'>
                            <h2 className='label-mono text-muted-foreground'>
                                Sur cette page
                            </h2>

                            <ul className='mt-3'>
                                {navLinks.map(link => (
                                    <li key={link.href}>
                                        <a href={link.href} className='inline-flex min-h-10 items-center hover:text-signal-text'>
                                            {link.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>
                </div>

                <p aria-hidden='true' className='heading mt-16 select-none text-[clamp(3.5rem,15.5vw,14rem)] leading-[0.8] tracking-[-0.045em] text-foreground/[0.06]'>
                    Kermarec
                </p>

                <Ruler className='mt-6 text-border' />

                <div className='mt-6 flex flex-col gap-2 text-sm text-muted-foreground sm:flex-row sm:justify-between'>
                    <p>
                        © <span suppressHydrationWarning>{new Date().getFullYear()}</span> {garage.name}. Prix TTC indicatifs, confirmés par devis écrit.
                    </p>

                    <p>
                        Taux horaire affiché&nbsp;: {garage.hourlyRate}&nbsp;€ HT
                    </p>
                </div>
            </Container>
        </footer>
    )
}
